import { useSelector } from 'react-redux';
import { ERROR_RECOGNITION_MODE, ErrorRecognitionActive, MainAppReducer, PlayInput } from 'Types';
import { FeatureFlag, isFeatureEnabled, useFeatureFlag } from 'Utils/FeatureFlags';
import { PreferredInputMode, toErrorRecognitionMode } from 'Utils/PreferredInputMode';

/**
 * Play Along (an internal name only; the app never shows it): whether the
 * player's notes are graded.
 *
 * The account records what the player plays on (`play_input`). Notes are graded
 * when the app can hear that input, which today means a MIDI keyboard. An
 * account that has not answered yet (no value) is read as a MIDI player, so
 * nothing changes for anyone who has not chosen otherwise. A MIDI player with no
 * keyboard connected is still graded: the existing MIDI blocker handles that
 * case, as it does today.
 *
 * With grading off the play surfaces still run (music, metronome, cursor), but
 * nothing is judged and nothing is earned:
 * - no note is matched or coloured, and adaptive tempo stays off;
 * - the accuracy readout and the chevrons stay blank;
 * - every tier of a level, and every repertoire piece, is open;
 * - no tier, level or piece completion is written, so the end-of-tier screens
 *   cannot come up;
 * - events that carry accuracy are not sent, and play time counts from pressing
 *   play.
 *
 * This is one switch read by the existing graded code paths, not a separate
 * play mode beside Game and Free Play.
 *
 * In development builds the Experimental Features switch
 * DEV_PLAY_INPUT_UNCONNECTED_PIANO overrides the account's value with
 * `unconnected_piano` on this device. It is forced off in production builds,
 * which read the account's value like any other build.
 */

/** What the development override plays on: a piano the app cannot hear. */
export const DEV_PLAY_INPUT_OVERRIDE: PlayInput = 'unconnected_piano';

/** True when the player's notes are graded: the input is MIDI, or not answered yet. */
export const isEffectivelyGraded = (playInput: PlayInput | null | undefined): boolean =>
  playInput == null || playInput === 'midi';

type PlayInputState = Partial<MainAppReducer>;

/** What the account says the player plays on (null when unanswered). */
export const selectAccountPlayInput = (state: PlayInputState): PlayInput | null =>
  state.mainAppReducer?.userData?.play_input ?? null;

/** The account's value, or the development override when it is on. */
export const effectivePlayInput = (
  accountPlayInput: PlayInput | null,
  devOverride: boolean,
): PlayInput | null => (devOverride ? DEV_PLAY_INPUT_OVERRIDE : accountPlayInput);

/** What the player plays on, for code outside React (sagas). */
export const selectEffectivePlayInput = (state: PlayInputState): PlayInput | null =>
  effectivePlayInput(
    selectAccountPlayInput(state),
    isFeatureEnabled(FeatureFlag.DEV_PLAY_INPUT_UNCONNECTED_PIANO),
  );

/** Grading for code outside React (sagas). */
export const selectEffectiveGrading = (state: PlayInputState): boolean =>
  isEffectivelyGraded(selectEffectivePlayInput(state));

/** What the player plays on, for components: re-renders when it changes. */
export function useEffectivePlayInput(): PlayInput | null {
  const accountPlayInput = useSelector(selectAccountPlayInput);
  const devOverride = useFeatureFlag(FeatureFlag.DEV_PLAY_INPUT_UNCONNECTED_PIANO);
  return effectivePlayInput(accountPlayInput, devOverride);
}

/** Grading for components: re-renders when the input or the override changes. */
export function useEffectiveGrading(): boolean {
  return isEffectivelyGraded(useEffectivePlayInput());
}

/**
 * The input mode an event records. Ungraded play is recorded as NONE, so it is
 * never mistaken for graded MIDI or audio play. Play session rows keep the input
 * in force and carry the separate `ungraded` flag instead (see
 * ungradedSessionFields).
 */
export const eventErrorRecognitionMode = (
  preferredInputMode: PreferredInputMode,
  graded: boolean,
): ERROR_RECOGNITION_MODE =>
  graded ? toErrorRecognitionMode(preferredInputMode) : ERROR_RECOGNITION_MODE.NONE;

/** Both hands graded: what graded sight reading always uses. */
export const ALL_HANDS_GRADED: ErrorRecognitionActive = { left: true, right: true };

/** Neither hand graded: what ungraded play uses, whatever the hand settings say. */
export const NO_HANDS_GRADED: ErrorRecognitionActive = { left: false, right: false };

/**
 * Extra fields for a new play session row: `ungraded: true` when grading is off,
 * nothing otherwise, so a graded session request is unchanged.
 */
export const ungradedSessionFields = (graded: boolean): { ungraded?: true } =>
  graded ? {} : { ungraded: true };
